import React, { useRef, useState, useEffect } from 'react';
import { KiteCard, KiteIcon } from '@kite/react-kite';
import { SmartLink } from '../SmartLink/SmartLink';
import { stripHtmlStrings } from '../../shared/stripHTMLStrings';
import './WebTable.scss';

export interface ITableImage {
    isLarge: boolean;
    name: string;
    src: string;
    alt: string;
}

export interface IWebTableInputs {
    title: string;

    isDark: boolean;
    description?: string;
    tableContent: string;

    highlightedRow: number;
    highlightedCol: number;
    tableImages: ITableImage[];
    ctaLink?: {
        text: string;
        url: string;
    };
}

export const WebTable = ({ inputs }: { inputs: IWebTableInputs }) => {
    const tableRef = useRef<HTMLDivElement>(null);
    const tableBoundRef = useRef<HTMLDivElement>(null);
    const [scrollLeft, setScrollLeft] = useState(``);
    const [hasOverflow, setHasOverflow] = useState(false);
    let tableParsingError = ``;
    const darkClassMod = inputs.isDark ? `--dark` : ``;

    // Gets the name of the image to insert
    const parseImageName = (td: string) => {
        const search = /\[\[(.*?)\]\]/;
        const imageName = td.match(search)?.[1]?.trim();
        return imageName;
    };

    // Gets the image data from the Drupal query for the image to add to the table cell
    const getCellImage = (
        imageName: string,
        tableImages: IWebTableInputs[`tableImages`]
    ) => {
        const cellImage = tableImages.find((image) => {
            return image.name?.trim() === imageName;
        });
        if (!cellImage) {
            tableParsingError = `Could not find a web table image by the convention: [[${imageName}]]. Please ensure this image name matches the name of a media image that is attached to the web table.`;
        }
        return cellImage;
    };

    // Parses the table string returned by Drupal and adds images to the table cells based on the authoring convention: [[image-name]]
    const addTableImages = (tableContent: IWebTableInputs[`tableContent`]) => {
        const tdRegex = /<(t[dh]\b).*?>.*?<(\/t[dh])>/g;
        const tdList = tableContent.match(tdRegex);

        return tdList.reduce((html, td) => {
            const isTh = td.includes(`<th>`);
            const imageName = parseImageName(td);
            if (!imageName) return html;

            const cellImage = getCellImage(imageName, inputs.tableImages);
            if (!cellImage) return html;

            const cellContents = stripHtmlStrings(td);
            const imgTextRegex = /\[\[(.*?)\]\]/;
            const innerText = cellContents.replace(imgTextRegex, ``).trim();
            // prettier-ignore
            const newCell = `
            <t${isTh ? `h`: `d`}>
                <div class="web-table__image-wrapper">
                    <img alt="${cellImage.alt}" src="${cellImage.src}" data-lg-img="${!!cellImage.isLarge}"  class="web-table__image ${cellImage.isLarge ? `web-table__image--large` : ``}" />
                </div>
                ${innerText}
            </t${isTh ? `h`: `d`}>`;
            return html.replace(td, newCell);
        }, tableContent);
    };

    // Note: row highlighting take precedence over column highlighting
    // This can be switched by changing the order of the 'if' statements
    let highlightClass = `hl-none${darkClassMod}`;

    if (inputs.highlightedCol) {
        highlightClass = `hl-col-${inputs.highlightedCol - 1}${darkClassMod}`;
    }

    if (inputs.highlightedRow && inputs.highlightedRow !== 0) {
        highlightClass = `hl-row-${inputs.highlightedRow - 1}${darkClassMod}`;
    }

    const scrollStyles = {
        left: scrollLeft,
    };

    const handleScroll = () => {
        if (!tableRef.current || !tableBoundRef.current) return;
        // Sets the scroll percentage based on how far the table has been scrolled relative to
        const scrollPercentage =
            (tableRef.current.scrollLeft /
                (tableRef.current.scrollWidth -
                    tableBoundRef.current.clientWidth)) *
            100;

        setScrollLeft(
            `max(calc(${scrollPercentage}% - ${
                0.2 * tableBoundRef.current.clientWidth
            }px), 0px)`
        );
    };

    // Checks if the table has overflow and sets the state accordingly
    const handleResize = () => {
        if (!tableRef.current || !tableBoundRef.current) return;
        if (
            tableRef.current.scrollWidth > tableBoundRef.current.scrollWidth &&
            !hasOverflow
        ) {
            setHasOverflow(true);
        } else if (
            tableRef.current.scrollWidth <= tableBoundRef.current.scrollWidth
        ) {
            setHasOverflow(false);
        }
    };

    const parsedHTML = addTableImages(inputs.tableContent);

    // Handles the scroll event for the table
    useEffect(() => {
        if (!tableRef.current) return;

        tableRef.current?.addEventListener(`scroll`, handleScroll);

        return () => {
            tableRef.current?.removeEventListener(`scroll`, handleScroll);
        };
    }, [tableRef, tableBoundRef]);

    useEffect(() => {
        window.addEventListener(`resize`, handleResize);
        return () => window.removeEventListener(`resize`, handleResize);
    }, []);

    // Set the overflow state on mount
    useEffect(() => {
        handleResize();
    }, [tableBoundRef.current, tableRef.current]);

    // If an image couldn't be found, the error message is displayed
    if (tableParsingError) {
        return (
            <div className="web-table">
                <p className="web-table__error">
                    <strong>ParsingError: </strong>
                    {tableParsingError}
                </p>
            </div>
        );
    }

    return (
        <KiteCard elevation={2}>
            <div
                className={`web-table ${
                    darkClassMod ? `web-table${darkClassMod}` : ``
                }`}
                ref={tableBoundRef}
            >
                <div className="web-table__header">
                    <h3 className="typestack-title-4 web-table__title">
                        {inputs.title}
                    </h3>
                    {inputs.description && (
                        <p className="typestack-body-2 web-table__description">
                            {inputs.description}
                        </p>
                    )}
                    {inputs.ctaLink && (
                        <SmartLink
                            url={inputs.ctaLink.url}
                            classNames={[`web-table__link typestack-title-6`]}
                        >
                            {inputs.ctaLink.text}
                            <KiteIcon icon="ki-arrow-right" size="1.5rem" />
                        </SmartLink>
                    )}
                </div>
                <div
                    className={`web-table__table ${
                        darkClassMod ? `web-table__table${darkClassMod}` : ``
                    }  ${highlightClass}`}
                    id="web-table"
                    tabIndex={0}
                    ref={tableRef}
                    role="region"
                    aria-label={`${inputs.title} table`}
                    dangerouslySetInnerHTML={{
                        __html: parsedHTML,
                    }}
                />
                {hasOverflow && (
                    <div className="visual-scroll">
                        <div className="visual-scroll__bar">
                            <div
                                className="visual-scroll__thumb"
                                style={scrollStyles}
                            />
                        </div>
                    </div>
                )}
            </div>
        </KiteCard>
    );
};
